import { AgGridReact } from "ag-grid-react";
import { useMemo, useRef } from "react";
import { useDetailTableEvents } from "src/hooks/detail-table/useDetailTableEvents";
import Grid_Excel from "src/components/common/gridWrapper/Grid_Excel";
import { GridContextWrapper } from "src/components/common/gridWrapper/GridContextWrapper";
import { useGridSettingContent } from "src/utils/grid/GridSettingContent";
import { defaultColDef } from "src/utils/grid/gridStyleUtils";
import { defaultEQM2070_R01DetailPivotDto, EQM2070_R01DetailPivotDto } from "./Dto_EQM2070_R01";
import { EQM2070DetailTabType, useEQM2070_R01Store } from "./Store_EQM2070_R01";
import {
	createDetailBaseColumnDefs,
	createDetailColumnDefs,
	createDetailDefaultColumnState,
	DETAIL_COLUMN_STATE_KEY,
	getMonthDayColumns_EQM2070_R01,
} from "./ColumnsDef_EQM2070_R01";
import { normalizeOxCellValue } from "./Util_EQM2070_R01";
import { getEditableDayColIds, parseDayColId } from "src/utils/date/dateUtils";
import { createCellCssClassRules, GridCellConfig, useCellChangeHandler } from "src/utils/grid/GridHandlerContent";
import { TableTab_EQM2070_R01 } from "./TableTab_EQM2070_R01";

interface Props {
	gridRef: React.RefObject<AgGridReact<EQM2070_R01DetailPivotDto> | null>;
}

export const Table_EQM2070_R01 = (props: Props) => {
	const { gridRef } = props;
	const {
		EQM2070_R01Filter,
		EQM2070_R01DetailPivotList,
		setEQM2070_R01DetailPivotList,
		originalEQM2070_R01DetailPivotList,
		updateEQM2070_R01DetailPivot,
		selectedEQM2070_R01DetailPivotList,
		deletedEQM2070_R01DetailPivotList,
		detailColumnState,
		setDetailColumnState,
		detailScrollTop,
		setDetailScrollTop,
	} = useEQM2070_R01Store();

	const gridWrapperRef = useRef<HTMLDivElement>(null);
	const activeDetailTab = EQM2070_R01Filter.equipmentCd as EQM2070DetailTabType;

	const {
		getRowId,
		paddedGridData,
		createCellClassRules,
	} = useGridSettingContent({
		gridRef,
		key: "valueIndex",
		gridData: EQM2070_R01DetailPivotList,
		defaultGridData: defaultEQM2070_R01DetailPivotDto,
		selectedRow: undefined,
		originalGridData: originalEQM2070_R01DetailPivotList,
		updateRow: (index, data) => updateEQM2070_R01DetailPivot(index, data),
	});

	const monthDayColumns = useMemo(
		() => getMonthDayColumns_EQM2070_R01(EQM2070_R01Filter.inspectDt)
	,[EQM2070_R01Filter.inspectDt]);

	const editableDayColIds = useMemo(
		() => getEditableDayColIds(monthDayColumns)
	,[monthDayColumns]);

	const cssCellConfig = useMemo<GridCellConfig>(() => ({
		disabledCells: [],
		insertedDisabledCells: [],
		commonDisabledCells: [],
		pointerCells: editableDayColIds,
		insertedPointerCells: [],
		commonPointerCells: [],
		alignLeftCells: ["inspectNm"],
	}), [editableDayColIds]);

	const {
		disabledInsertedColumns,
		disabledColumns,
		pointerInsertedColumns,
		pointerColumns,
		alignLeftColumns,
	} = useMemo(() => createCellCssClassRules(cssCellConfig), [cssCellConfig]);

	const cellCssDefaultColDef = useMemo(() => {
		const baseRules = createCellClassRules<EQM2070_R01DetailPivotDto>({
			idKey: "valueIndex",
			selectedList: selectedEQM2070_R01DetailPivotList,
			deletedList: deletedEQM2070_R01DetailPivotList,
			disabledInsertedColumns,
			disabledColumns,
			pointerInsertedColumns,
			pointerColumns,
			alignLeftColumns,
		});
		return { ...defaultColDef, cellClassRules: { ...baseRules } };
	}, [createCellClassRules, selectedEQM2070_R01DetailPivotList, deletedEQM2070_R01DetailPivotList, disabledInsertedColumns, disabledColumns, pointerInsertedColumns, pointerColumns, alignLeftColumns]);

	const baseColumnDefs = useMemo(
		() => createDetailBaseColumnDefs(EQM2070_R01Filter.inspectDt),
	[EQM2070_R01Filter.inspectDt]);

	const defaultColumnState = useMemo(
		() => createDetailDefaultColumnState(baseColumnDefs),
	[baseColumnDefs]);

	const columnDefs = useMemo(
		() => createDetailColumnDefs(baseColumnDefs, detailColumnState),
	[baseColumnDefs, detailColumnState]);

	const handleCellChange = useCellChangeHandler({
		dataList: EQM2070_R01DetailPivotList,
		originalDataList: originalEQM2070_R01DetailPivotList,
		updateRow: updateEQM2070_R01DetailPivot,
		customBeforeUpdate: (updatedData, field, newValue) => {
			const dayDd = parseDayColId(field);
			if (!dayDd) {
				return updatedData;
			}
			const normalizedValue = normalizeOxCellValue(newValue);
			const originalRow = originalEQM2070_R01DetailPivotList.find(
				(row) => row.valueIndex === updatedData.valueIndex,
			);
			const originalDayValue = originalRow?.dayValues?.[dayDd] ?? "";
			const isModified = updatedData.inserted
				? !!updatedData.isModified
				: String(originalDayValue) !== String(normalizedValue);
			const { [field]: _removed, ...rest } = updatedData as EQM2070_R01DetailPivotDto &
				Record<string, unknown>;
			return {
				...rest,
				dayValues: {
					...updatedData.dayValues,
					[dayDd]: normalizedValue,
				},
				isModified,
			} as EQM2070_R01DetailPivotDto;
		},
	});

	const defaultEditableColumns = editableDayColIds;

	const cellEventHandlers = useDetailTableEvents({
		detailList: EQM2070_R01DetailPivotList,
		editableColumns: defaultEditableColumns,
		keyDownConfig: {
			enableArrowNavigation: true,
		},
	});

	const pinnedBottomRowData = useMemo(
		() => [{ inspectNm: `${EQM2070_R01DetailPivotList.length.toLocaleString()}건` }]
	,[EQM2070_R01DetailPivotList]);

	return (
		<div className="w-full h-full flex flex-col">
			<TableTab_EQM2070_R01 />

			<div key={activeDetailTab} className="flex-1 min-h-0">
				<GridContextWrapper<EQM2070_R01DetailPivotDto>
					gridWrapperRef={gridWrapperRef}
					gridRef={gridRef}
					paddedGridData={paddedGridData}
					setPaddedGridData={setEQM2070_R01DetailPivotList}
					defaultRowData={defaultEQM2070_R01DetailPivotDto}
					baseColumnDefs={detailColumnState ? columnDefs : baseColumnDefs}
					defaultColumnState={defaultColumnState}
					setColumnState={setDetailColumnState}
					localStorageKey={DETAIL_COLUMN_STATE_KEY}
					menuItems={undefined}
				>
					<Grid_Excel<EQM2070_R01DetailPivotDto>
						ref={gridRef}
						rowData={paddedGridData}
						getRowId={getRowId}
						columnDefs={columnDefs}
						defaultColDef={cellCssDefaultColDef}
						columnStateKey={DETAIL_COLUMN_STATE_KEY}
						setColumnState={setDetailColumnState}
						getDefaultColumnState={() => defaultColumnState}
						scrollTop={detailScrollTop}
						setScrollTop={setDetailScrollTop}
						onCellValueChanged={handleCellChange}
						onCellClicked={cellEventHandlers.handleCellClick}
						onCellKeyDown={cellEventHandlers.handleCellKeyDown}
						pinnedBottomRowData={pinnedBottomRowData}
					/>
				</GridContextWrapper>
			</div>
		</div>
	);
};
